/* external */
import React, { useMemo, useState, useRef } from "react";
import {
  Badge,
  Button,
  Checkbox,
  Dropdown,
  IconDisclosure,
  SearchInput,
  Tooltip,
} from "@toddle-edu/ds-web";
import {
  ChevronDownOutlined,
  ChevronRightOutlined,
  ChevronUpOutlined,
  FilterOutlined,
  FolderFilled,
} from "@toddle-edu/ds-icons";
import classNames from "classnames";
import _ from "lodash";

/* internal */
import { ELEMENT_TYPE_MYP_OBJECTIVES } from "Constants/stringConstants";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import AnimateHeight from "UIComponents/AnimateHeight";
import FocusTrapWrapper from "UIComponents/FocusTrapWrapper/FocusTrapWrapper";
import { KeyboardNavigableList } from "UIComponents/KeyboardNavigableList";
import { onEnterOrSpaceKeyDown } from "Utils";

/* relative */
import classes from "./MoreFilters.scss";
import FilterOptions from "./FilterOptions/FilterOptions";
import { getFilterLabel } from "../../../modules/Utils";

const FilterContainer = props => {
  const {
    filters,
    t,
    handleFilterVisibleChange,
    activeTab,
    curriculumType,
    plannerElementSets,
    parentFiltersData,
    appliedFilters,
    curriculumProgramId,
    updateInputField,
    updateFilterOptions,
    isProjectNode,
    projectNodeDetails,
    currentTabFiltersConfig,
    activeTabClassesFilterOptions,
    testId,
  } = props;

  const localAppliedFilters = structuredClone(appliedFilters);
  _.forEach(localAppliedFilters, (values, key) => {
    if (_.every(values, value => _.isEmpty(_.get(value, "values")))) return;

    const finalValues = _.concat(
      ..._.map(values, value => {
        const childValues = _.get(value, "values", []);
        const childKey = _.get(value, "key", "");

        if (
          _.every(childValues, childValue =>
            _.isEmpty(_.get(childValue, "values"))
          )
        ) {
          return _.map(childValues, value => `${childKey}.${value}`);
        }

        return _.concat(
          ..._.map(childValues, childValue =>
            _.map(
              _.get(childValue, "values"),
              value => `${childKey}.${_.get(childValue, "key")}.${value}`
            )
          )
        );
      })
    );
    _.set(localAppliedFilters, key, finalValues);
  });

  const [localFiltersData, setLocalFiltersData] = useState(localAppliedFilters);
  const [selectedFilterKey, setSelectedFilterKey] = useState(
    _.get(_.first(filters), "filter")
  );
  const [searchText, setSearchText] = useState("");
  const [expandedAccordionKeys, setExpandedAccordionKeys] = useState([]);

  const sidebarRef = useRef(null);

  const selectedFilterOptions = _.get(localFiltersData, [
    selectedFilterKey,
    "options",
  ]);

  const selectedFilterValues = _.get(localFiltersData, [selectedFilterKey]);

  const selectedFiltersCount = useMemo(
    () =>
      _.reduce(
        _.values(
          _.filter(localFiltersData, (value, key) => {
            return _.includes(_.map(filters, "filter"), key);
          })
        ),
        (count, filter) => {
          const isFilterSelected = _.size(filter) > 0;

          return isFilterSelected ? count + 1 : count;
        },
        0
      ),
    [localFiltersData]
  );

  const isNestedCheckbox = _.some(
    selectedFilterOptions,
    filterOption => !_.isEmpty(_.get(filterOption, "options", []))
  );

  const handleFilterApply = () => {
    _.forEach(filters, config => {
      if (config.filter !== ELEMENT_TYPE_MYP_OBJECTIVES) {
        const updatedFilters = _.get(localFiltersData, config.filter);
        if (updatedFilters) {
          updateInputField({ key: config.filter, values: updatedFilters });
        }
      } else {
        const updatedFilters = _.get(localFiltersData, config.filter);

        if (updatedFilters) {
          const splitValues = _.map(updatedFilters, updatedFilter =>
            _.split(updatedFilter, ".")
          );
          const secondLevelFilters = _.mapValues(
            _.groupBy(splitValues, _.first),
            values => _.map(values, value => _.slice(value, 1))
          );

          const thirdLevelFilters = _.mapValues(
            secondLevelFilters,
            (filters, key) => {
              if (key === "INTERDISCIPLINARY_CRITERIA") {
                return _.concat(...filters);
              } else {
                return _.mapValues(_.groupBy(filters, _.first), values =>
                  _.concat(..._.map(values, value => _.slice(value, 1)))
                );
              }
            }
          );

          const finalFilterObject = _.map(thirdLevelFilters, (values, key) => {
            if (key === "INTERDISCIPLINARY_CRITERIA") {
              return { key, values };
            } else {
              return {
                key,
                values: _.map(values, (value, key) => ({
                  key,
                  values: value,
                })),
              };
            }
          });

          updateInputField({ key: config.filter, values: finalFilterObject });
        }
      }
    });
    handleFilterVisibleChange();
  };

  const handleFilterChange = ({ isChecked, values }) => {
    setLocalFiltersData(localFiltersData => {
      const updatedLocalFiltersData = _.clone(localFiltersData);

      let selectedFilterValues = _.get(
        updatedLocalFiltersData,
        selectedFilterKey,
        []
      );
      if (isChecked) {
        selectedFilterValues.push(...values);
      } else {
        selectedFilterValues = _.filter(
          selectedFilterValues,
          option => !_.includes(values, option)
        );
      }

      updatedLocalFiltersData[selectedFilterKey] = selectedFilterValues;

      return updatedLocalFiltersData;
    });
  };

  const getAllCheckboxComp = ({
    totalOptionsCount,
    allValues,
    allOptionLabel,
    allOptionPrefix,
  }) => {
    const isAllChecked = totalOptionsCount === _.size(selectedFilterValues);
    const isAnyChecked = !isAllChecked && _.size(selectedFilterValues) > 0;

    const handleAllChange = () => {
      setLocalFiltersData(localFiltersData => {
        const updatedLocalFiltersData = _.clone(localFiltersData);
        updatedLocalFiltersData[selectedFilterKey] = isAllChecked
          ? []
          : allValues;

        return updatedLocalFiltersData;
      });
    };

    const allTextLabelClasses = classNames("text-body", classes.allTextLabel);
    const checkboxWrapperClasses = classNames(classes.checkboxWrapper, {
      [classes.rowReverseFlexBtwn]: !!allOptionPrefix,
    });

    return (
      <>
        <div className={classes.filterOption}>
          <Checkbox
            isChecked={isAllChecked}
            isIndeterminate={isAnyChecked}
            dsVersion="2.0"
            checkboxWrapperClassName={checkboxWrapperClasses}
            onChange={handleAllChange}
            testId={
              testId ? `${testId}-moreFilters-filters-allCheckbox` : undefined
            }
          >
            <div className={classes.allTextContent}>
              {allOptionPrefix ? allOptionPrefix : null}
              <div className={allTextLabelClasses}>
                {allOptionLabel ? allOptionLabel : t("common:all")}
              </div>
            </div>
          </Checkbox>
        </div>
        {isNestedCheckbox ? <div className={classes.divider} /> : null}
      </>
    );
  };

  const getCheckboxComp = ({
    isChildOption = false,
    filterOption = {},
    isLast = false,
    entityType,
  }) => {
    const {
      label,
      value,
      key,
      options,
      isArchived = false,
      color,
    } = filterOption;

    let prefixElement;
    if (entityType === "EVIDENCE_FOLDER") {
      prefixElement = <FolderFilled style={{ color }} />;
    }

    const isChildParent = !_.isEmpty(options);

    const isChecked = isChildParent
      ? _.every(_.map(options, "value"), item =>
          _.includes(selectedFilterValues, item)
        )
      : _.includes(selectedFilterValues, value);
    const isIndeterminate = isChildParent
      ? _.some(_.map(options, "value"), item =>
          _.includes(selectedFilterValues, item)
        )
      : false;

    const doesChildrenHaveChildren = isChildParent
      ? !_.isEmpty(_.get(_.first(options), "options"))
      : false;

    const contentWrapperClasses = classNames({
      [classes.alignCenterContent]: entityType === "EVIDENCE_FOLDER",
    });

    const labelClasses = classNames({
      [classes.label]: true,
      ["text-body"]: !isChildOption && !isNestedCheckbox,
      ["text-label"]: !isChildOption && isNestedCheckbox,
    });

    const headingClasses = classNames("text-label", classes.accordionHeader, {
      [classes.childOptions]: isChildOption,
    });

    const checkboxWrapperClasses = classNames(classes.checkboxWrapper, {
      [classes.rowReverseFlexBtwn]: !!prefixElement,
    });

    const getAllValues = options => {
      return _.concat(
        ..._.map(options, ({ options: childOptions, value }) => {
          if (childOptions) {
            return getAllValues(childOptions);
          }
          return [value];
        })
      );
    };

    const handleAccordionToggle = () => {
      setExpandedAccordionKeys(prev => {
        if (_.includes(prev, optionKey)) {
          return _.filter(prev, prevValue => prevValue !== optionKey);
        } else {
          return [...prev, optionKey];
        }
      });
    };

    const checkboxChangeHandler = () => {
      handleFilterChange({
        isChecked: !isChecked,
        values: isChildParent ? getAllValues(options) : [value],
        isChildOption,
      });
    };

    const handleAccordionKeydown = e => {
      e.stopPropagation();
      onEnterOrSpaceKeyDown(e, handleAccordionToggle);
    };

    const handleCheckboxKeyDown = e => {
      e.stopPropagation();
      onEnterOrSpaceKeyDown(e, checkboxChangeHandler);
    };

    const optionKey = value || key;

    const isAccordionExpanded =
      !_.isEmpty(searchText) || _.includes(expandedAccordionKeys, optionKey);

    const filterOptionClasses = classNames(classes.filterOption, {
      [classes.archivedFilterOption]: isArchived,
    });

    return (
      <ConditionalWrapper
        wrapper={({ children }) => (
          <div
            onKeyDown={handleAccordionKeydown}
            className={classes.accordionWrapper}
          >
            <button
              onClick={handleAccordionToggle}
              className={headingClasses}
              data-test-id={
                testId
                  ? `${testId}-moreFilters-filterAccordion-${optionKey}`
                  : undefined
              }
            >
              <span dir="auto">{label}</span>
              {isAccordionExpanded ? (
                <ChevronUpOutlined variant="subtle" />
              ) : (
                <ChevronDownOutlined variant="subtle" />
              )}
            </button>
            <AnimateHeight
              duration={300}
              height={isAccordionExpanded ? "auto" : 0}
              style={{ width: "100%" }}
            >
              <div
                className={classNames({
                  [classes.accordionChildren]:
                    !isChildOption &&
                    optionKey !== "INTERDISCIPLINARY_CRITERIA",
                })}
              >
                {children}
              </div>
            </AnimateHeight>
          </div>
        )}
        condition={isChildParent}
      >
        <React.Fragment key={optionKey}>
          {!doesChildrenHaveChildren ? (
            <div
              onKeyDown={handleCheckboxKeyDown}
              className={filterOptionClasses}
            >
              <Checkbox
                dsVersion="2.0"
                checkboxWrapperClassName={checkboxWrapperClasses}
                isChecked={isChecked}
                isIndeterminate={!isChecked && isIndeterminate}
                testId={
                  testId
                    ? `${testId}-moreFilters-filterOptions-${key ?? value}`
                    : undefined
                }
                onChange={checkboxChangeHandler}
              >
                <div className={contentWrapperClasses}>
                  {React.isValidElement(prefixElement) ? prefixElement : null}

                  <Tooltip dsVersion="2.0" tooltip={label} placement="top">
                    <div dir="auto" className={labelClasses}>
                      {isChildParent ? t("common:all_label", { label }) : label}
                    </div>
                  </Tooltip>
                </div>
              </Checkbox>
            </div>
          ) : null}

          {isChildParent
            ? _.map(options, option =>
                getCheckboxComp({
                  isChildOption: true,
                  filterOption: option,
                  isGrandChild: isChildOption,
                })
              )
            : null}

          {isNestedCheckbox && !isChildOption && !isLast ? (
            <div className={classes.divider} />
          ) : null}
        </React.Fragment>
      </ConditionalWrapper>
    );
  };

  const handleSearch = searchValue => {
    setSearchText(searchValue);
  };

  const getSearchBar = ({ placeholder }) => {
    return (
      <>
        <div className={classes.searchBarContainer}>
          <SearchInput
            dsVersion="2.0"
            isDropdownSearch={true}
            placeholder={placeholder}
            value={searchText}
            onChange={handleSearch}
            // * due to focus change on entering space in searchInput dropdown getting closed
            onKeyUp={e => {
              e.preventDefault();
            }}
            testId={testId ? `${testId}-moreFilters-search` : undefined}
          />
        </div>
        <div className={classes.divider} />
      </>
    );
  };

  const renderFilter = ({ config = {} }) => {
    const entityTypeInfo = _.get(
      currentTabFiltersConfig,
      `${activeTab}.entityTypeInfo`,
      {}
    );

    const dependentFiltersConfig = _.get(
      currentTabFiltersConfig,
      `${activeTab}.dependentFilterConfig`,
      {}
    );

    const filter = config.filter;

    const filterData = _.get(entityTypeInfo, filter, {});

    const dependentFilterParents = _.get(
      _.find(
        dependentFiltersConfig,
        ({ filter: dependentFilter }) => dependentFilter === filter
      ),
      "parents",
      []
    );

    const {
      filterLocale,
      isLabelPresentInSetLabels,
      labelPresentInSetLabelsPerm,
      filterByLearningCourse = false,
    } = filterData;

    const label = getFilterLabel({
      plannerElementSets,
      t,
      filterLocale,
      labelPresentInSetLabelsPerm,
      isLabelPresentInSetLabels,
      entityType: filter,
      curriculumType,
    });

    return (
      <FilterOptions
        t={t}
        getSearchBar={getSearchBar}
        getAllCheckboxComp={getAllCheckboxComp}
        getCheckboxComp={getCheckboxComp}
        searchText={searchText}
        entityType={filter}
        parentFiltersData={parentFiltersData}
        values={appliedFilters[filter]}
        dependentFilterParents={dependentFilterParents}
        {...config}
        label={label}
        curriculumProgramId={curriculumProgramId}
        updateInputField={updateInputField}
        updateFilterOptions={updateFilterOptions}
        baseSetType={filterData.baseSetType}
        curriculumType={curriculumType}
        isProjectNode={isProjectNode}
        projectNodeDetails={projectNodeDetails}
        activeTabClassesFilterOptions={activeTabClassesFilterOptions}
        activeTab={activeTab}
        filterByLearningCourse={filterByLearningCourse}
      />
    );
  };

  return (
    <FocusTrapWrapper isActive={true}>
      <div className={classes.top}>
        <KeyboardNavigableList listContainerRef={sidebarRef}>
          <div ref={sidebarRef} className={classes.filterList}>
            {_.map(filters, ({ filter }) => {
              const selectedOptionsCount = _.size(
                _.get(localFiltersData, [filter])
              );

              const filterClasses = classNames({
                ["text-body"]: true,
                [classes.filter]: true,
                [classes.selectedFilter]: _.isEqual(filter, selectedFilterKey),
              });

              const handleFilterItemSelect = e => {
                if (!_.isEmpty(filter)) {
                  setSelectedFilterKey(filter);
                  setSearchText("");
                }
              };

              const entityTypeInfo = _.get(
                currentTabFiltersConfig,
                `${activeTab}.entityTypeInfo`,
                {}
              );

              const filterData = _.get(entityTypeInfo, filter, {});

              const {
                filterLocale,
                isLabelPresentInSetLabels,
                labelPresentInSetLabelsPerm,
              } = filterData;

              const label = getFilterLabel({
                plannerElementSets,
                t,
                filterLocale,
                labelPresentInSetLabelsPerm,
                isLabelPresentInSetLabels,
                entityType: filter,
                curriculumType,
              });

              return (
                <button
                  className={filterClasses}
                  onClick={handleFilterItemSelect}
                  key={filter}
                  data-test-id={
                    testId
                      ? `${testId}-moreFilters-filterType-${filter}`
                      : undefined
                  }
                >
                  <Tooltip tooltip={label} placement="top">
                    <span dir="auto" className={classes.filterLabel}>
                      {label}
                    </span>
                  </Tooltip>
                  <div className={classes.iconContainer}>
                    {selectedOptionsCount > 0 ? (
                      <Badge
                        type="numeric"
                        value={selectedOptionsCount}
                        size="xx-small"
                        variant="subtle"
                        dsVersion="2.0"
                      />
                    ) : null}
                    <ChevronRightOutlined size="xx-small" variant="subtle" />
                  </div>
                </button>
              );
            })}
          </div>
        </KeyboardNavigableList>
        <div className={classes.filterOptionList}>
          {renderFilter({
            config: _.find(
              filters,
              ({ filter }) => filter === selectedFilterKey,
              {}
            ),
          })}
        </div>
      </div>
      <div className={classes.footer}>
        {selectedFiltersCount > 0 ? (
          <div className={"text-label"}>
            {t("classRoom:count_filter_applied", {
              count: selectedFiltersCount,
            })}
          </div>
        ) : null}
        <div className={classes.rightFooter}>
          <Button
            variant="neutral"
            dsVersion="2.0"
            type="outlined"
            onClick={handleFilterVisibleChange}
            testId={
              testId ? `${testId}-moreFilters-filters-cancelFilter` : undefined
            }
          >
            {t("common:cancel")}
          </Button>
          <Button
            variant="progressive"
            dsVersion="2.0"
            type="fill"
            onClick={handleFilterApply}
            testId={
              testId ? `${testId}-moreFilters-filters-applyFilter` : undefined
            }
          >
            {t("common:apply")}
          </Button>
        </div>
      </div>
    </FocusTrapWrapper>
  );
};

const MoreFilters = props => {
  const {
    filters,
    t,
    filterContainerProps,
    activeMoreFiltersCount,
    currentTabFiltersConfig,
    activeTabClassesFilterOptions,
    testId,
  } = props;

  const [isFilterVisible, setIsFilterVisible] = useState(false);

  const handleFilterVisibleChange = () => {
    setIsFilterVisible(prevIsVisible => !prevIsVisible);
  };

  return (
    <Dropdown
      placement="bottomRight"
      dsVersion={"2.0"}
      visible={isFilterVisible}
      onVisibleChange={handleFilterVisibleChange}
      overlay={
        <div className={classes.filtersContainer}>
          <FilterContainer
            {...filterContainerProps}
            filters={filters}
            t={t}
            handleFilterVisibleChange={handleFilterVisibleChange}
            currentTabFiltersConfig={currentTabFiltersConfig}
            activeTabClassesFilterOptions={activeTabClassesFilterOptions}
            testId={testId}
          />
        </div>
      }
    >
      <IconDisclosure
        variant={"neutral"}
        type={"outlined"}
        dsVersion="2.0"
        icon={<FilterOutlined />}
        badgeProps={{
          value: activeMoreFiltersCount,
          variant: "dark",
          shape: "circle",
        }}
        aria-label={t("common:more_label", { label: t("common:filters") })}
        testId={testId ? `${testId}-moreFilters` : undefined}
      />
    </Dropdown>
  );
};

export default MoreFilters;
